/* start - flights_split_bounds_us */
import { useClientFetch } from "hooks/useClientFetch";
import useReloadIfSearchStoreMissing, { isSearchStoreInitial } from "hooks/useReloadIfSearchStoreMissing";
import { useSearchAPIParams } from "hooks/useSearchAPIParams";
import React, { Profiler, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSearch } from "store/search/selectors";
import { actions as LoaderActions } from "../store/loadingScreen/actions";
import { actions as SearchResultsBoundsActions } from "../store/searchResultsBounds/actions";
import { actions as SearchCriteriaActions } from "../store/searchCriteria/actions";
import { Route, useLocation, useParams } from "react-router-dom";
import useRouteName from "hooks/useRouteName";
import StaticContext from "app/StaticContext/StaticContext";
import useClientMetrics from "hooks/useClientMetrics";
import { PageHeader } from "./Search";
import { AppShellDesktop } from "components/elements/AppShell";
import Container from "components/elements/Container";
import { Box, HiddenVisually, Stack, Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useSearchbox from "hooks/useSearchbox";
import useLocaleContext from "hooks/useLocaleContext";
import { SearchboxController, SearchBoxHorizontalDefault } from "@bookingcom/flights-searchbox";
import { trackExperiment } from "utils/et";
import flights_web_reduce_spacing_sr_ddot from "utils/experiments/funnel/flights_web_reduce_spacing_sr_ddot";
import { UISearchResults } from "@flights/types";
import useRemoveExternalParamsFromUrl from "hooks/useRemoveExternalParamsFromUrl";
import { customGoal, stage } from "@flights/et-universal";
import flights_web_cat_fly_anywhere_filters_www from "utils/experiments/customer-aquisition/flights_web_cat_fly_anywhere_filters_www";
import { getSearchCriteria } from "store/getSearchCriteria";
import { getSearchFetchStatus } from "store/searchResultsBounds/inboundSelectors";
import SearchLoadingScreen from "components/elements/LoadingScreen/components/SearchLoadingScreen";
import SearchResultsInboundDesktop from "components/elements/SearchResults/SearchResults.inbound.desktop";
import { FLIGHT_DETAILS_INBOUND_ROUTE_PATH } from "@flights/constants";
import { HomeDesktop, FlightDetailsDesktop, SearchOutboundDesktop } from "app/routes";
import useMeasureSearchToFlightDetails from "hooks/useMeasureSearchToFlightDetails";
import { getMarketingTrackingVariable } from "@flights/web-api-utils-universal";

const SearchInboundDesktop = () => {
  const search = useSelector(getSearch);
  const fetchStatus = useSelector(getSearchFetchStatus);
  const dispatch = useDispatch();

  const location = useLocation<{ hideLoader: boolean; forceRefetch: boolean }>();
  const routeName = useRouteName();
  const staticContext = useContext(StaticContext);
  const i18n = useI18n();

  const { trackRenderTime } = useClientMetrics();
  const { onFetchedResults, onSelectFlight } = useMeasureSearchToFlightDetails();

  const queryParams = new URLSearchParams(location.search);
  if (process.env.BUILD_TARGET === "server" && staticContext.set) {
    const { getInitialState } = require("../server/screens/search");
    staticContext.set({ getInitialState: getInitialState(queryParams) });
  }

  useRemoveExternalParamsFromUrl();

  useEffect(() => {
    stage("mvsf_whs_launchpad_nav_products_reorder_www", 7);

    const caSource = new URLSearchParams(window.location.search).get("ca_source")?.toLowerCase();

    if (caSource === "flyanywhere") {
      flights_web_cat_fly_anywhere_filters_www.goals.clicked_card_2();
    } else if (caSource === "flights_index_cf") {
      customGoal("flights_web_cat_popular_nodate_www", 1);
    }
  }, []);

  // start -- FETCH DATA
  const params = useParams<{ outboundToken: string }>();
  const searchAPIParams = useSearchAPIParams({ inboundFor: params.outboundToken });
  const url = `/api/flights/?${searchAPIParams}`;
  const isMetaOrigin = getMarketingTrackingVariable("ext-origin");

  useClientFetch(url, {
    // delay the load of Search Results in background of Flight Details
    // when user lands on SR with a meta offerToken to temporarily fix an issue on ETG
    delayFetchBy: !!isMetaOrigin && routeName.includes("searchresults") ? 500 : 0,
    condition: () => {
      if (isSearchStoreInitial(search)) {
        return false;
      }

      return true;
    },
    fetch: () => {
      dispatch(SearchResultsBoundsActions.fetchInbound());
    },
    success: (data: UISearchResults) => {
      dispatch(SearchCriteriaActions.setSearchCriteria(getSearchCriteria(queryParams, search)));
      dispatch(
        SearchResultsBoundsActions.fetchSuccessInbound({
          ...data,
          // We only need the first second for inbound.
          flightOffers: data.flightOffers.map((f) => ({ ...f, segments: [f.segments[1]] }))
        })
      );
      dispatch(LoaderActions.hide());
      onFetchedResults();
    },
    error: (data) => {
      dispatch(LoaderActions.hide());
      dispatch(SearchResultsBoundsActions.fetchErrorInbound(data));
    },
    forceRefetch: location?.state?.forceRefetch
  });
  // END -- fetch data

  const searchBoxProps = useSearchbox();
  const { isRTL } = useLocaleContext();
  const searchBoxControllerProps = { ...searchBoxProps, i18n, defaultSegmentLength: search.searchSegments.length };

  const [preloadDetails, toggle] = useState(false);
  if (!preloadDetails && fetchStatus === "success") {
    toggle(true);
    SearchOutboundDesktop.preload();
    FlightDetailsDesktop.preload();
    HomeDesktop.preload();
  }

  const hideSBBag = !!trackExperiment("flights_web_ddot_sb_bg_color");
  const hideBackground = !!flights_web_reduce_spacing_sr_ddot.trackWithDefaultStage();

  if (useReloadIfSearchStoreMissing(search)) {
    return null;
  }

  return (
    <Profiler id="screen" onRender={trackRenderTime}>
      <PageHeader segments={search.searchSegments} />
      <AppShellDesktop>
        <Stack grow={true} gap={hideBackground ? 0 : 6}>
          <Box
            padding={0}
            backgroundColor={hideSBBag || hideBackground ? undefined : "neutral_alt"}
            mixin={{ paddingBlockStart: 6, paddingBlockEnd: hideSBBag ? 0 : 6 }}
          >
            <Container>
              <HiddenVisually>
                <Text tagName="h1">{i18n.trans(t("a11y_flights_sr_search_hidden_heading"))}</Text>
              </HiddenVisually>
              <SearchboxController {...searchBoxControllerProps} isRTL={isRTL}>
                {() => <SearchBoxHorizontalDefault />}
              </SearchboxController>
            </Container>
          </Box>

          <Container>
            <SearchResultsInboundDesktop onSelectFlight={onSelectFlight} />
          </Container>

          <Route key={FLIGHT_DETAILS_INBOUND_ROUTE_PATH} path={FLIGHT_DETAILS_INBOUND_ROUTE_PATH} exact={true}>
            <FlightDetailsDesktop />
          </Route>

          {routeName.includes("searchresults") && <SearchLoadingScreen />}
        </Stack>
      </AppShellDesktop>
    </Profiler>
  );
};

export default SearchInboundDesktop;
/* end - flights_split_bounds_us */
